import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Accordion, Box, useSlotRecipe} from '@chakra-ui/react3'
import ModuleContainer from '../../components/module-container'
import Heading from '../../components/cms/heading'
import Fade from '../../components/cms/fade'
import {hasValidString, hasValidVideoContent, hasValidImageSrc} from '../../utils/utils'
import accordionSlotRecipe from './index.recipe'
import {StylesProvider} from './context'
import AccordionItem from './item'

const ASSET_WIDTHS = {
    left: {
        base: '97vw',
        s: '44vw',
        l: '734px'
    },
    center: {
        base: '97vw',
        s: '848px'
    }
}

/**
 * Accordion module displays collapsible content sections with optional heading.
 * Each accordion item can contain text, images, videos, and call-to-action buttons.
 * Only one item can be expanded at a time, following WAI-ARIA accordion pattern.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {string} [props.anchor] - Optional anchor ID for linking to this module
 * @param {'default'|'subtle'|'strong'|'inverse'} [props.background='default'] - Background style
 * @param {'default'|'reduced'|'min'|'min-top','min-bottom'} [props.spacing='default'] - Background style
 * @param {number} [props.headingLevel=2] - HTML heading level (1-6) for the main heading
 * @param {Object} [props.heading] - Heading configuration object
 * @param {string} [props.heading.headline1] - First part of main heading
 * @param {string} [props.heading.headline2] - Second part of main heading
 * @param {string} [props.heading.subline] - Optional subline text
 * @param {boolean} [props.heading.separateLines=false] - Whether to display headlines on separate lines
 * @param {boolean} [props.heading.dualTone=false] - Whether to apply dual-tone styling
 * @param {'default'|'small'|'hidden'} [props.heading.variant='default'] - Heading size variant
 * @param {'left'|'center'} [props.alignment='left'] - Horizontal alignment of content
 * @param {boolean} [props.firstExpanded=false] - Whether first item starts expanded
 * @param {Array<Object>} props.items - Array of accordion items (1-9 items)
 * @param {string} props.items[].headline - Item heading text (4+ characters)
 * @param {Object} [props.items[].copy] - Item copy text object
 * @param {string} [props.items[].copy.text] - Copy text content
 * @param {Object} [props.items[].cta] - Optional call-to-action button
 * @param {string} [props.items[].cta.label] - CTA button label
 * @param {string} [props.items[].cta.variant] - CTA button variant (primary|secondary|text)
 * @param {Object} [props.items[].cta.action] - CTA action configuration
 * @param {Object} [props.items[].asset] - Optional image or video asset
 * @returns {JSX.Element|null} Rendered accordion module or null if no valid items
 */
const AccordionModule = React.forwardRef(
    (
        {
            moduleId,
            moduleType,
            pageId,
            anchor,
            background = 'default',
            headingLevel = 2,
            heading,
            alignment = 'left',
            firstExpanded = false,
            items = [],
            spacing
        },
        ref
    ) => {
        const recipe = useSlotRecipe({recipe: accordionSlotRecipe})
        const styles = useMemo(() => recipe({alignment}), [recipe, alignment])
        const assetWidths = ASSET_WIDTHS[alignment]

        // Filter items to only include those with valid headlines
        const validItems = useMemo(
            () =>
                items.filter(
                    (item) =>
                        hasValidString(item?.headline) &&
                        (hasValidVideoContent(item.asset) ||
                            hasValidImageSrc(item.asset) ||
                            hasValidString(item.cta?.label) ||
                            hasValidString(item.copy?.text))
                ),
            [items]
        )

        // Return null if no valid items
        if (validItems.length === 0) return null

        // Determine default expanded items
        const defaultValue = firstExpanded ? ['item-0'] : []

        return (
            <ModuleContainer
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                css={styles.container}
                spacing={spacing}
                ref={ref}
            >
                <StylesProvider value={styles}>
                    {heading && (
                        <Box css={styles.heading}>
                            <Fade>
                                <Heading
                                    {...heading}
                                    headingLevel={headingLevel}
                                    alignment={alignment}
                                />
                            </Fade>
                        </Box>
                    )}

                    <Accordion.Root
                        collapsible
                        defaultValue={defaultValue}
                        multiple={false}
                        css={styles.accordion}
                    >
                        {validItems.map((item, index) => (
                            <AccordionItem
                                {...item}
                                alignment={alignment}
                                key={index}
                                value={`item-${index}`}
                                widths={assetWidths}
                                moduleType={moduleType}
                            />
                        ))}
                    </Accordion.Root>
                </StylesProvider>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    AccordionModule.displayName = 'AccordionModule'
}

const assetPropType = PropTypes.shape({
    type: PropTypes.oneOf(['image', 'direct', 'brightcove']),
    // Image props
    src: PropTypes.string,
    disBaseLink: PropTypes.string,
    alt: PropTypes.string,
    title: PropTypes.string,
    // Video props
    poster: PropTypes.object,
    autoplay: PropTypes.bool,
    videoControls: PropTypes.bool,
    // Direct video
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string,
            type: PropTypes.string,
            breakpoint: PropTypes.string
        })
    ),
    // Brightcove video
    brightcoveId: PropTypes.string
})

AccordionModule.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    copy: PropTypes.shape({
        text: PropTypes.string
    }),
    headingLevel: PropTypes.number,
    heading: PropTypes.shape({
        headline1: PropTypes.string,
        headline2: PropTypes.string,
        subline: PropTypes.string,
        separateLines: PropTypes.bool,
        dualTone: PropTypes.bool,
        variant: PropTypes.oneOf(['default', 'small', 'hidden'])
    }),
    alignment: PropTypes.oneOf(['left', 'center']),
    firstExpanded: PropTypes.bool,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            headline: PropTypes.string.isRequired,
            copy: PropTypes.shape({
                text: PropTypes.string
            }),
            cta: PropTypes.shape({
                label: PropTypes.string,
                variant: PropTypes.oneOf(['primary', 'secondary', 'text']),
                screenReader: PropTypes.string,
                action: PropTypes.shape({
                    type: PropTypes.oneOf(['category', 'content', 'product', 'url', 'anchor']),
                    value: PropTypes.string
                }),
                openInNewTab: PropTypes.bool
            }),
            asset: assetPropType
        })
    ),
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom'])
}

export default AccordionModule
